import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Character,
  CharacterDiceTemplate,
  CharacterDiceTemplateRequest,
  CharacterRequest,
  CampaignListItem,
} from "../../types/types";
import { CharacterService } from "../../services/characterService";
import { useFormValidation } from "../../hooks";
import { commonValidationRules } from "../../utils/validation";
import { Button, ConfirmationDialog, Input, TextArea, Select } from "../../components/ui";
import { DiceTemplateManager } from "../../components/business";
import { CampaignService } from "../../services/campaignService";

const CharacterDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [character, setCharacter] = useState<Character | null>(null);
  const [diceTemplates, setDiceTemplates] = useState<CharacterDiceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState<CampaignListItem[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [campaignsMap, setCampaignsMap] = useState<Map<number, string>>(new Map());

  const form = useFormValidation<CharacterRequest>({
    initialValues: {
      name: "",
      description: "",
      imageUrl: "",
      notes: "",
      campaignId: null,
    },
    validationRules: {
      name: commonValidationRules.name,
      description: commonValidationRules.description,
      imageUrl: commonValidationRules.url,
    },
    onSubmit: async (values) => {
      if (!character) return;
      const updated = await CharacterService.updateCharacter(character.id, values);
      setCharacter(updated);
      setEditMode(false);
    },
  });

  useEffect(() => {
    if (id) {
      loadCharacterData(parseInt(id));
    }
  }, [id]);

  // Scroll to dice templates section if hash is present
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "#dice-templates") {
      setTimeout(() => {
        const element = document.getElementById("dice-templates-section");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  // Fetch available campaigns for the edit form dropdown
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoadingCampaigns(true);
        const campaigns = await CampaignService.getAvailableCampaigns();
        setAvailableCampaigns(campaigns);
        const map = new Map<number, string>();
        campaigns.forEach((c) => map.set(c.id, c.name));
        setCampaignsMap(map);
      } catch (err) {
        console.error("Failed to fetch campaigns:", err);
      } finally {
        setLoadingCampaigns(false);
      }
    };
    fetchCampaigns();
  }, []);

  const loadCharacterData = async (characterId: number) => {
    setLoading(true);
    setError(null);
    try {
      const [characterData, templatesData] = await Promise.all([
        CharacterService.getCharacter(characterId),
        CharacterService.getCharacterDiceTemplates(characterId),
      ]);
      setCharacter(characterData);
      setDiceTemplates(templatesData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load hero");
    } finally {
      setLoading(false);
    }
  };

  const handleEnterEditMode = () => {
    if (!character) return;
    form.setValues({
      name: character.name,
      description: character.description,
      imageUrl: character.imageUrl,
      notes: character.notes,
      campaignId: character.campaignId ?? null,
    });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
  };

  const handleDeleteCharacter = () => {
    if (!character) return;
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteCharacter = async () => {
    if (!character) return;
    try {
      await CharacterService.deleteCharacter(character.id);
      navigate("/protected/characters");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete hero");
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const handleCreateDiceTemplate = async (templateData: CharacterDiceTemplateRequest) => {
    if (!character) return;
    try {
      const newTemplate = await CharacterService.createDiceTemplate(character.id, templateData);
      setDiceTemplates((prev) => [...prev, newTemplate]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create dice template");
    }
  };

  const handleUpdateDiceTemplate = async (
    templateId: number,
    templateData: CharacterDiceTemplateRequest
  ) => {
    if (!character) return;
    try {
      const updated = await CharacterService.updateDiceTemplate(
        character.id,
        templateId,
        templateData
      );
      setDiceTemplates((prev) =>
        prev.map((template) => (template.id === templateId ? updated : template))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update dice template");
    }
  };

  const handleDeleteDiceTemplate = async (templateId: number) => {
    if (!character) return;
    try {
      await CharacterService.deleteDiceTemplate(character.id, templateId);
      setDiceTemplates((prev) => prev.filter((template) => template.id !== templateId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete dice template");
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-950 text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-b-2 border-amber-400 mx-auto mb-4"></div>
          <p className="text-zinc-300">Loading hero...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-zinc-950 text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={() => navigate("/protected/characters")} variant="secondary" size="md">
            Back to Heroes
          </Button>
        </div>
      </div>
    );
  }

  if (!character) {
    return (
      <div className="bg-zinc-950 text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-300 mb-4">Hero not found</p>
          <Button onClick={() => navigate("/protected/characters")} variant="secondary" size="md">
            Back to Heroes
          </Button>
        </div>
      </div>
    );
  }

  const campaignName = character.campaignId ? campaignsMap.get(character.campaignId) : undefined;

  return (
    <div className="bg-zinc-950 text-white min-h-screen p-8">
      <div className="space-y-6">
        {/* Character Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate("/protected/characters")}
              className="text-xs text-zinc-500 hover:text-amber-400 transition-colors flex items-center gap-1 mb-2"
            >
              <i className="fas fa-arrow-left"></i> Back to Heroes
            </button>
            <h1 className="text-2xl font-light tracking-wide">{character.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={editMode ? handleCancelEdit : handleEnterEditMode}
              className={`px-4 py-2 text-sm transition-colors ${
                editMode
                  ? "bg-amber-400 text-zinc-950 hover:bg-amber-300"
                  : "bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700"
              }`}
            >
              {editMode ? "Cancel Edit" : "Edit Hero"}
            </button>
            <button
              onClick={handleDeleteCharacter}
              className="px-4 py-2 bg-red-900/40 border border-red-800/60 text-red-400 text-sm hover:bg-red-900/60 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>

        {/* View Mode */}
        {!editMode && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Avatar */}
            <div
              className="bg-zinc-900 border border-zinc-800 p-6 flex items-center justify-center"
              style={{ minHeight: "200px" }}
            >
              {character.imageUrl ? (
                <img
                  src={character.imageUrl}
                  alt={character.name}
                  className="max-w-full max-h-48 object-cover border border-zinc-700"
                />
              ) : (
                <div className="text-center">
                  <div className="w-24 h-24 bg-zinc-800 border border-zinc-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-user text-zinc-500 text-3xl"></i>
                  </div>
                  <p className="text-xs text-zinc-600">No image uploaded</p>
                </div>
              )}
            </div>

            {/* Character Info */}
            <div className="md:col-span-2 bg-zinc-900 border border-zinc-800 p-6">
              <h2 className="text-sm font-semibold text-amber-400 tracking-wide uppercase mb-4">
                Hero Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Name</p>
                  <p className="text-sm text-white">{character.name}</p>
                </div>
                {character.campaignId && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Chronicle</p>
                    <button
                      onClick={() => navigate(`/protected/campaigns/${character.campaignId}`)}
                      className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                    >
                      {campaignName || `Chronicle #${character.campaignId}`}
                    </button>
                  </div>
                )}
                {character.description && (
                  <div className="col-span-2">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
                      Description
                    </p>
                    <p className="text-sm text-zinc-300">{character.description}</p>
                  </div>
                )}
                {character.notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Notes</p>
                    <div className="bg-zinc-800 border border-zinc-700 p-3">
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap">{character.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit Mode */}
        {editMode && (
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <h2 className="text-sm font-semibold text-amber-400 tracking-wide uppercase mb-4">
              Edit Hero
            </h2>
            <form onSubmit={form.handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    label="Hero Name"
                    value={form.values.name}
                    onChange={form.handleChange("name")}
                    onBlur={form.handleBlur("name")}
                    error={form.errors.name}
                    fullWidth
                    variant="filled"
                  />
                </div>
                <div>
                  <Select
                    key={`campaign-select-${availableCampaigns.length}-${form.values.campaignId}`}
                    id="campaignId"
                    name="campaignId"
                    label="Chronicle (Optional)"
                    value={form.values.campaignId?.toString() || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      form.setValue("campaignId", value === "" ? null : parseInt(value, 10));
                    }}
                    onBlur={form.handleBlur("campaignId")}
                    error={form.errors.campaignId}
                    options={[
                      { value: "", label: "Select a chronicle (optional)" },
                      ...availableCampaigns.map((c) => ({
                        value: c.id.toString(),
                        label: c.name,
                      })),
                    ]}
                    fullWidth
                    variant="filled"
                    disabled={loadingCampaigns}
                  />
                </div>
              </div>
              <div>
                <TextArea
                  id="description"
                  name="description"
                  label="Description"
                  value={form.values.description}
                  onChange={form.handleChange("description")}
                  onBlur={form.handleBlur("description")}
                  error={form.errors.description}
                  rows={3}
                  fullWidth
                  variant="filled"
                />
              </div>
              <div>
                <TextArea
                  id="notes"
                  name="notes"
                  label="Notes"
                  value={form.values.notes}
                  onChange={form.handleChange("notes")}
                  onBlur={form.handleBlur("notes")}
                  error={form.errors.notes}
                  rows={4}
                  fullWidth
                  variant="filled"
                />
              </div>
              <div>
                <Input
                  type="url"
                  id="imageUrl"
                  name="imageUrl"
                  label="Hero Image URL (Optional)"
                  value={form.values.imageUrl}
                  onChange={form.handleChange("imageUrl")}
                  onBlur={form.handleBlur("imageUrl")}
                  error={form.errors.imageUrl}
                  fullWidth
                  placeholder="https://..."
                  variant="filled"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={form.isSubmitting}
                  className="px-5 py-2 bg-amber-400 text-zinc-950 text-sm font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
                >
                  {form.isSubmitting ? "Saving..." : "Save Changes"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-5 py-2 bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm hover:bg-zinc-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Dice Templates Section */}
        <DiceTemplateManager
          templates={diceTemplates}
          onCreateTemplate={handleCreateDiceTemplate}
          onUpdateTemplate={handleUpdateDiceTemplate}
          onDeleteTemplate={handleDeleteDiceTemplate}
          loading={loading}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        title="Delete Hero"
        message="Are you sure you want to delete this hero? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteCharacter}
        onCancel={() => setShowDeleteConfirmation(false)}
        variant="danger"
      />
    </div>
  );
};

export default CharacterDetail;
